/* eslint-disable @typescript-eslint/no-unused-vars */
import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from 'src/redux/api/baseQuery';
import { IcargarArchivoRequest, IdocumentosSoporteRequest, IdocumentosSoporteResponse, IeditarDocumentoSoporteRequest, IeditarDocumentoSoporteResponse, IguardarArchivoRequest, IguardarArchivoResponse, IguardarDocumentoSoporteRequest, IguardarDocumentoSoporteResponse, IobtenerArchivoConsolidadoRequest, IobtenerArchivoRequest, IobtenerMatrizRequest } from './types/documentosSoporte';

/**
 * API para la gestión de documentos soporte
 * @see https://redux-toolkit.js.org/rtk-query/overview
 * @see https://redux-toolkit.js.org/rtk-query/usage/customizing-queries
 *
 */
export const documentosSoporteApi = createApi({
	reducerPath: 'documentosSoporteApi',
	baseQuery,
	tagTypes: ['documentosSoporte'],
	endpoints: (builder) => ({
		obtenerDocumentosSoporte: builder.query<IdocumentosSoporteResponse, IdocumentosSoporteRequest>({
			query({ proyectoId, paginador }) {
				return {
					url: 'v1/docs-soporte',
					method: 'GET',
					credentials: 'include',
					params: {
						proyectoId,
						...paginador
					},
				};
			},
			// TODO: Proveer por proyecto id
			providesTags: ['documentosSoporte']
		}),
		guardarDocumentoSoporte: builder.mutation<IguardarDocumentoSoporteResponse, IguardarDocumentoSoporteRequest>({
			query(body) {
				return {
					url: 'v1/docs-soporte',
					method: 'POST',
					credentials: 'include',
					body,
				};
			},
			invalidatesTags: ['documentosSoporte'],
		}),
		editarDocumentoSoporte: builder.mutation<IeditarDocumentoSoporteResponse, IeditarDocumentoSoporteRequest>({
			query({ docSoporteId, ...body }) {
				return {
					url: `v1/docs-soporte/${docSoporteId}`,
					method: 'PUT',
					credentials: 'include',
					body,
				};
			},
			invalidatesTags: ['documentosSoporte'],
		}),
		obtenerArchivo: builder.query<Blob, IobtenerArchivoRequest>({
			query({ adjuntoId }) {
				return {
					url: `adjuntos/descargar`,
					method: 'GET',
					credentials: 'include',
					params:{adjuntoId},
					responseHandler: (response) => response.blob(),
				};
			},
		}),
		obtenerArchivoConsolidado: builder.query<Blob, IobtenerArchivoConsolidadoRequest>({
			query({ aspiranteId }) {
				return {
					url: `adjuntos/consolidado`,
					method: 'GET',
					credentials: 'include',
					params:{aspiranteId},
					responseHandler: (response) => response.blob(),
				};
			},
		}),

		obtenerMatriz: builder.query<Blob, IobtenerMatrizRequest>({
			query(params) {
				return {
					url: `reportes/inscritos`,
					method: 'GET',
					credentials: 'include',
					params,
					responseHandler: (response) => response.blob(),
				};
			},
		}),
		guardarArchivo: builder.mutation<IguardarArchivoResponse, IguardarArchivoRequest>({
			query({ file, proyectoId }) {
				const formData = new FormData();
				formData.append('adjunto', file);
				return {
					url: 'v1/adjuntos',
					method: 'POST',
					credentials: 'include',
					params: { proyectoId },
					body: formData,
				};
			},
			invalidatesTags: ['documentosSoporte'],
		}),
		cargarArchivo: builder.mutation<IguardarArchivoResponse, IcargarArchivoRequest>({
			query({ file, aspiranteId }) {
				const formData = new FormData();
				formData.append('archivo', file);
				formData.append('aspiranteId', aspiranteId);
				return {
					url: 'adjuntos/cargar',
					method: 'POST',
					credentials: 'include',
					body: formData,
				};
			},
			invalidatesTags: ['documentosSoporte'],
		}),
	}),
});

export const {
	useObtenerDocumentosSoporteQuery,
	useGuardarDocumentoSoporteMutation,
	useEditarDocumentoSoporteMutation,
	useLazyObtenerArchivoQuery,
	useLazyObtenerArchivoConsolidadoQuery,
	useGuardarArchivoMutation,
	useCargarArchivoMutation,
	useLazyObtenerMatrizQuery
} = documentosSoporteApi;
